/**
 * Generate a JSON template with merge field placeholders
 * for integration with templating engines (Mustache, Handlebars, etc.)
 */

interface SchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  format?: string;
  [key: string]: unknown;
}

interface JsonSchema {
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  [key: string]: unknown;
}

/**
 * Generate a JSON template with {{field}} placeholders
 */
export function generateJsonTemplate(
  schema: object,
  selectedFields: string[],
  requiredOverrides: Record<string, boolean>
): string {
  const schemaObj = schema as JsonSchema;
  const properties = schemaObj.properties || {};
  
  const template: Record<string, string> = {};
  
  selectedFields.forEach(fieldName => {
    const prop = properties[fieldName];
    if (!prop) return;
    
    // Use field name as merge field variable (snake_case)
    const mergeFieldName = fieldName.replace(/-/g, '_');
    template[fieldName] = `{{${mergeFieldName}}}`;
  });
  
  return JSON.stringify(template, null, 2);
}

/**
 * Generate a JavaScript/TypeScript template literal string
 * that shows how to use the merge fields programmatically
 */
export function generateTemplateUsageExample(
  schema: object,
  selectedFields: string[],
  requiredOverrides: Record<string, boolean>,
  standardName: string
): string {
  const schemaObj = schema as JsonSchema;
  const properties = schemaObj.properties || {};
  const required = schemaObj.required || [];
  
  const lines: string[] = [
    `/**`,
    ` * ${standardName} Template`,
    ` * Generated merge field template for use with templating engines`,
    ` */`,
    ``,
    `// Interface for the data object`,
    `interface ${standardName.replace(/[^a-zA-Z0-9]/g, '')}Data {`,
  ];
  
  selectedFields.forEach(fieldName => {
    const prop = properties[fieldName];
    if (!prop) return;
    
    const isRequired = requiredOverrides[fieldName] ?? required.includes(fieldName);
    const tsType = mapJsonTypeToTs(prop.type, prop.enum);
    const optionalMark = isRequired ? '' : '?';
    
    if (prop.description) {
      lines.push(`  /** ${prop.description} */`);
    }
    lines.push(`  ${fieldName}${optionalMark}: ${tsType};`);
  });
  
  lines.push(`}`);
  lines.push(``);
  lines.push(`// Template with merge fields`);
  lines.push(`const template = ${JSON.stringify(generateJsonTemplateObject(properties, selectedFields), null, 2)};`);
  lines.push(``);
  lines.push(`// Example usage with your data`);
  lines.push(`function populateTemplate(data: ${standardName.replace(/[^a-zA-Z0-9]/g, '')}Data): object {`);
  lines.push(`  return Object.fromEntries(`);
  lines.push(`    Object.entries(template).map(([key, value]) => {`);
  lines.push(`      const fieldName = value.replace(/{{|}}/g, '');`);
  lines.push(`      return [key, data[fieldName as keyof typeof data] ?? value];`);
  lines.push(`    })`);
  lines.push(`  );`);
  lines.push(`}`);
  
  return lines.join('\n');
}

function mapJsonTypeToTs(type: string | undefined, enumValues?: string[]): string {
  if (enumValues && enumValues.length > 0) {
    return enumValues.map(v => `'${v}'`).join(' | ');
  }
  
  switch (type) {
    case 'string':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'unknown[]';
    case 'object':
      return 'Record<string, unknown>';
    default:
      return 'unknown';
  }
}

function generateJsonTemplateObject(
  properties: Record<string, SchemaProperty>,
  selectedFields: string[]
): Record<string, string> {
  const template: Record<string, string> = {};
  
  selectedFields.forEach(fieldName => {
    if (properties[fieldName]) {
      const mergeFieldName = fieldName.replace(/-/g, '_');
      template[fieldName] = `{{${mergeFieldName}}}`;
    }
  });
  
  return template;
}
